import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { Button } from 'react-bootstrap';
import axios from "axios";
import TreeViewerMUI from "../../components/TreeViewerMUI";
import { useMediaQuery, useTheme, Paper, TextField, MenuItem, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useReferenceData } from '../../components/ReferenceDataContext';
import { Organisation } from '../../models/Organisation';
import dayjs from 'dayjs';
import '../../components/StyleSheets/Visibility.css';
import { Artist } from '../../models/artist';
import ArtistSelector from '../../components/ArtistManagement/ArtistSelector';
import { Artwork } from "../../models/Artwork";
import { AppCategory } from "../../models/Category";
import { useCurrency } from '../../components/CurrencyContext';

//Valuation Inputs
import ValueInput, { ValueData } from '../../components/ValuationInput/ValuationInput';

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { createApiClient, useApiClient } from "../../components/useApiClient";
import { useSafeRequest } from "../../components/useSafeRequest";
import { useIsAuthenticated } from "@azure/msal-react";

//Category Tree
import CategoryPickerField from "../../components/CategoryPickerField";

//Asset Dimensions
import AssetDimensions, { AssetDimensionValues } from "../../components/AssetDimensions";
import { ArtworkMeasurementType, ArtworkMeasurement } from "../../models/ArtworkMeasurement";

//Provenance
import { ArtworkProvenance } from "../../models/ArtworkProvenance";

//Error Handling
import { useErrorHandler, isNullOrEmpty } from '../../hooks/useErrorHandler';
import ErrorSummary from '../ErrorHandler/ErrorSummary';

import {
    ProvenanceForm,
    ProvenanceFormHandle
} from "../../components/Provenance/ProvenanceForm";

import "./ArtworkFormStyleSheet.css";

type ArtworkPageProps = {
    artworkId?: number | null;
    isOpen: boolean;
    onClose?: () => void;
    onSaveComplete?: () => void;
};


const ArtworkManagement: React.FC<ArtworkPageProps> = ({ artworkId, isOpen = false, onClose, onSaveComplete }) => {

    const { errors, handleError, clearErrors, validateRequired, hasErrors } = useErrorHandler();

    const { currency } = useCurrency();
    const navigate = useNavigate();
    const provenanceRef = useRef<ProvenanceFormHandle | null>(null);
    const [provenanceDetails, setProvenanceDetails] = useState<ArtworkProvenance | undefined>();

    const theme = useTheme();
    const isEditMode = artworkId !== null;
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const gridTemplateColumns = isEditMode ? "1fr" : "1fr 1fr";
    const maxWidth: "sm" | "md" | "lg" = isEditMode ? "md" : "lg";

    const { currencies, sources, locations, isReady, loadingAny, errorAny, refresh } = useReferenceData();
    const [organisations, setOrganisations] = useState<Organisation[]>([]);
    const [artists, setArtists] = useState<Artist[]>([]);

    const { activeClientId, getAccessToken, isAdmin, isOwner } = useUser();
    const previousClientId = useRef<number | null>(activeClientId);

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const isAuthenticated = useIsAuthenticated();
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [editTitle, setEditTitle] = useState("");
    const [editArtworkCreationDate, setEditArtworkCreationDate] = useState<string | undefined>("");
    const [editDescription, setEditDescription] = useState<string | undefined>("");
    const [editMedium, setEditMedium] = useState<string | undefined>("");
    const [editCategoryId, setEditCategoryId] = useState<number | null>(0);
    const [editValue, setEditValue] = useState(0);
    const [editCurrency, setEditCurrency] = useState("GBP");
    const [editArtistId, setEditArtistId] = useState<number | null>();
    const [editLocationId, setEditLocationId] = useState(0);
    const [editSourceOrganisationId, setEditSourceOrganisationId] = useState<number | ''>('');
    const [categories, setCategories] = useState <AppCategory[] | null>([]);

    const [artworkUpdate, setArtworkUpdate] = useState<Artwork | null>(null);

    //Artist Selection
    const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);

    const handleArtistSelect = (artist: Artist | null) => {
        setSelectedArtist(artist);
        setEditArtistId(artist?.artistId || null);
    };  

    const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

    const handleLocationChange = (newLocationId: string) => {
        setSelectedLocationId(Number(newLocationId));
        setEditLocationId(Number(newLocationId));
    }

    const [formValues_NotUsed, setFormValues_NotUsed] = useState<AssetDimensionValues>({
        height: "",
        width: "",
        depth: "",
        dimensionUnit: "cm",
        weight: "",
        weightUnit: "kg",
    });

    const createDefaultMeasurements = (): ArtworkMeasurement[] => [
        {
            artworkMeasurementId: null,
            measurementType: ArtworkMeasurementType.Artwork,
            height: "",
            width: "",
            depth: "",
            dimensionUnit: "cm",
            weight: "",
            weightUnit: "kg",
        }
    ];

    const [measurements, setMeasurements] =
        useState<ArtworkMeasurement[]>(createDefaultMeasurements);

    const getMeasurement = (type: ArtworkMeasurementType) =>
        measurements.find(m => m.measurementType === type);

    const artworkMeasurement = getMeasurement(
        ArtworkMeasurementType.Artwork
    );

    const framedMeasurement = getMeasurement(
        ArtworkMeasurementType.Framed
    );

    const handleMeasurementChange = (
        type: ArtworkMeasurementType,
        field: keyof AssetDimensionValues,
        value: string | number
    ) => {
        setMeasurements(prev =>
            prev.map(m =>
                m.measurementType === type
                    ? {
                        ...m,
                        [field]: value
                    }
                    : m
            )
        );
    };

    const handleAmountChange = (valueData: ValueData) => {
        if (valueData.isValid) {
            setEditValue(valueData.numericValue ?? 0);
        }
    };

    const toDimensionValues = ( measurement: ArtworkMeasurement ): AssetDimensionValues => ({
        height: measurement.height ?? "",
        width: measurement.width ?? "",
        depth: measurement.depth ?? "",
        dimensionUnit: measurement.dimensionUnit ?? "cm",
        weight: measurement.weight ?? "",
        weightUnit: measurement.weightUnit ?? "kg"
    });

    const addFramedMeasurement = () => {
        setMeasurements(prev => {
            if (prev.some(
                m => m.measurementType === ArtworkMeasurementType.Framed
            )) {
                return prev;
            }

            const artworkMeasurement = prev.find(
                m => m.measurementType === ArtworkMeasurementType.Artwork
            );

            return [
                ...prev,
                {
                    artworkMeasurementId: null,
                    measurementType: ArtworkMeasurementType.Framed,
                    height: "",
                    width: "",
                    depth: "",
                    dimensionUnit: artworkMeasurement?.dimensionUnit ?? "cm",
                    weight: "",
                    weightUnit: artworkMeasurement?.weightUnit ?? "kg",
                }
            ];
        });
    };

    const removeFramedMeasurement = () => {
        setMeasurements(prev =>
            prev.filter(
                m => m.measurementType !== ArtworkMeasurementType.Framed
            )
        );
    };

    useEffect(() => {
        if (!artworkUpdate)
            return;

        if (artworkUpdate.measurements?.length) {
            setMeasurements(
                artworkUpdate.measurements.map(m => ({
                    artworkMeasurementId: m.artworkMeasurementId,
                    measurementType: m.measurementType,
                    height: m.height ?? "",
                    width: m.width ?? "",
                    depth: m.depth ?? "",
                    dimensionUnit: m.dimensionUnit ?? "cm",
                    weight: m.weight ?? "",
                    weightUnit: m.weightUnit ?? "kg",
                }))
            );
        }
    }, [artworkUpdate]);

    // *** Setup safe API Calls ***
    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );
    const { get, put, post, delete: del } = useApiClient();

    const orgReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });
    const artistReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });
    const artworkReq = useSafeRequest<any>(api, { maxAttempts: 3 });
    const catReq = useSafeRequest<any>(api, { maxAttempts: 3 });

    const resetForm = () => {

        clearErrors();

        setSelectedArtist(null);
        setEditTitle("");
        setEditArtworkCreationDate(undefined);
        setEditCategoryId(null);
        setEditDescription("");
        setEditMedium("");
        setEditLocationId(0);
        setSelectedLocationId(null);
        setEditCurrency(currency);
        setEditValue(0);

        //Dimensions
        const dimensions: AssetDimensionValues = {
            height: "",
            width: "",
            depth: "",
            dimensionUnit: "cm",
            weight: "",
            weightUnit: "kg"
        };

        //setFormValues(dimensions);

        setMeasurements(
            artworkUpdate?.measurements?.length
                ? artworkUpdate.measurements.map(m => ({
                    artworkMeasurementId: m.artworkMeasurementId,
                    measurementType: m.measurementType,
                    height: m.height ?? "",
                    width: m.width ?? "",
                    depth: m.depth ?? "",
                    dimensionUnit: m.dimensionUnit ?? "cm",
                    weight: m.weight ?? "",
                    weightUnit: m.weightUnit ?? "kg",
                }))
                : [
                    {
                        artworkMeasurementId: null,
                        measurementType: ArtworkMeasurementType.Artwork,
                        height: "",
                        width: "",
                        depth: "",
                        dimensionUnit: "cm",
                        weight: "",
                        weightUnit: "kg",
                    }
                ]
        );

        //Provenance
        const dateString = undefined;
        const provDetails: ArtworkProvenance = {
            dateAcquired: dateString,
            sourceId: 0,
            sourceName: '',
            provenance: '',
            notes: '',
        }

        setProvenanceDetails(provDetails);
    }

    const getData = async () => {

        setDataLoading(true);

        const tasks = [
            orgReq.run({ method: "GET", url: 'organisations' }),
            artistReq.run({ method: "GET", url: `artists/for-client/${clientIdRef.current}` }),
            catReq.run({ method: "GET", url: 'categories' }),

        ];

        const [o, ar, cat] = await Promise.allSettled(tasks);

        if (o.status === "fulfilled") setOrganisations(o.value);
        if (ar.status === "fulfilled") setArtists(ar.value);
        if (cat.status === "fulfilled") {
            setCategories(cat.value);
        }

        if (artworkId) {
            const getArtworkTask = [
                artworkReq.run({ method: "GET", url: `artworks/${artworkId}` }),
            ];

            // const [art] = await Promise.allSettled(getArtworkTask);

            // if (art.status === "fulfilled") {
            //     setArtworkUpdate(art.value);
            // }

            const accessToken = await getAccessToken();

            if (accessToken) {

                const artwork = await get<Artwork>(`artworks/${artworkId}`, accessToken);
                setArtworkUpdate(artwork);
                setMeasurements(
                    artwork.measurements?.length
                        ? artwork.measurements.map(m => ({
                            artworkMeasurementId: m.artworkMeasurementId,
                            measurementType: m.measurementType,
                            height: m.height ?? "",
                            width: m.width ?? "",
                            depth: m.depth ?? "",
                            dimensionUnit: m.dimensionUnit ?? "cm",
                            weight: m.weight ?? "",
                            weightUnit: m.weightUnit ?? "kg",
                        }))
                        : createDefaultMeasurements()
                );
            }

        }

        setDataLoading(false);
    };

    const handleSave = async () => {

        const provenanceValues: ArtworkProvenance =
            provenanceRef.current?.getValues() ?? {
                dateAcquired: "",
                sourceId: 0,
                sourceName: "",
                provenance: "",
                notes: "",
            };

        //Validate Form
        clearErrors();
        let isValid = true;

        const validateArtistId = editArtistId === 0 ? null : selectedArtist?.artistId.toString() ?? null;
        const validateCategoryId = editCategoryId === 0 ? null : editCategoryId?.toString() ?? null;
        const validateLocationId = selectedLocationId === 0 ? null : selectedLocationId?.toString() ?? null;

        if (!validateRequired(validateArtistId, 'Artist', 'Please select an artist.')) isValid = false;
        if (!validateRequired(editTitle, 'Title', '')) isValid = false;
        if (!validateRequired(editDescription, 'Description', '')) isValid = false;
        if (!validateRequired(validateCategoryId, 'Category', 'Please select and apply a category.')) isValid = false;
        if (!validateRequired(validateLocationId, 'Location', 'Please select a location.')) isValid = false;

        if (!isEditMode) {
            if (!validateRequired(provenanceValues?.dateAcquired, 'Date Acquired', '')) isValid = false;
        }

        if (isValid) {
            const bResult = await saveChanges();

            if (bResult) {
                if (onClose) onClose(); // close modal after save
                onSaveComplete?.();
            }
        }
    };

    const saveChanges = async () => {
        const accessToken = await getAccessToken();

        if (!accessToken) {
            setError("User not authenticated or token expired.");
        } else {
            try {

                const provenanceValues: ArtworkProvenance =
                    provenanceRef.current?.getValues() ?? {
                        dateAcquired: "",
                        sourceId: 0,
                        sourceName: "",
                        provenance: "",
                        notes: "",
                    };

                const formData = new FormData();

                const updateLocationId = selectedLocationId ?? editLocationId;
                const parsed = dayjs(provenanceValues?.dateAcquired, "DD-MM-YYYY", true);
                const dateToUse = parsed.isValid() ? parsed.format("YYYY-MM-DD") : '';
                const updateArtworkId = artworkId ? artworkId.toString() : "0";

                formData.append("ArtworkId", updateArtworkId);
                formData.append("ArtWorkTitle", editTitle);
                formData.append("ArtworkCreationDate", editArtworkCreationDate || "");
                formData.append("ArtDescription", editDescription || "");
                formData.append("Medium", editMedium || "");
                formData.append("ArtistId", editArtistId?.toString() || "");
                formData.append("CategoryId", editCategoryId?.toString() || "");
                formData.append("LocationId", updateLocationId?.toString() || "");

                //Dimension Info
                // formData.append('Height', formValues.height?.toString() || '');
                // formData.append('Width', formValues.width?.toString() || '');
                // formData.append('Depth', formValues.depth?.toString() || '');
                // formData.append('DimensionUnits', formValues.dimensionUnit || '');
                // formData.append('Weight', formValues.weight?.toString() || '');
                // formData.append('WeightUnits', formValues.weightUnit || '');

                measurements.forEach((m, index) => {

                    if (m.artworkMeasurementId != null) {
                        formData.append(
                            `Measurements[${index}].ArtworkMeasurementId`,
                            m.artworkMeasurementId.toString()
                        );
                    }

                    formData.append(
                        `Measurements[${index}].MeasurementType`,
                        m.measurementType.toString()
                    );

                    formData.append(
                        `Measurements[${index}].Height`,
                        m.height?.toString() ?? ""
                    );

                    formData.append(
                        `Measurements[${index}].Width`,
                        m.width?.toString() ?? ""
                    );

                    formData.append(
                        `Measurements[${index}].Depth`,
                        m.depth?.toString() ?? ""
                    );

                    formData.append(
                        `Measurements[${index}].DimensionUnit`,
                        m.dimensionUnit
                    );

                    formData.append(
                        `Measurements[${index}].Weight`,
                        m.weight?.toString() ?? ""
                    );

                    formData.append(
                        `Measurements[${index}].WeightUnit`,
                        m.weightUnit
                    );
                });

                if (updateArtworkId != "0") {
                    await put(`artworks/${updateArtworkId}`, accessToken, formData);
                } else {
                    formData.append("DateAcquired", dateToUse);
                    formData.append("EstimatedValue", editValue.toString());
                    formData.append("CurrencyCode", editCurrency);
                    formData.append("Provenance", provenanceValues.provenance);
                    formData.append("SourceId", provenanceValues.sourceId?.toString() || "");
                    formData.append("SourceOrganisationId", editSourceOrganisationId?.toString() || "");
                    formData.append("SourceDescription", provenanceValues.notes?.toString() || "");
                    formData.append("SourceName", provenanceValues.sourceName?.toString() || "");

                    const newArtworkId = await post(`artworks/upload`, accessToken, formData);
                        navigate(`/artwork/${newArtworkId}`)
                }

                return true;

            }
            catch (e: any) {
                const data = e?.response?.data;
                if (data?.code === "LICENSE_LIMIT_EXCEEDED") {
                    setError(`${data.message} (${data.current}/${data.max})`);
                    handleError({
                        code: 'Save', message: 'Error saving artwork', details: `${data.message} (${data.current}/${data.max})`
                    });
                    return false;
                }
                setError("Error saving artwork.");
            }
        }
    };

    useEffect(() => {
        if (previousClientId.current !== null && previousClientId.current !== activeClientId) {
            // Close modal on client change
            if (onClose) onClose();
        }
        previousClientId.current = activeClientId;
    }, [activeClientId]);

    useEffect(() => {
        //Populate Page
        if (artworkUpdate) {
            const assignedArtist: Artist = {
                artistId: artworkUpdate.artist.artistId,
                artistName: artworkUpdate.artistName,
                source: artworkUpdate.artist.source,
                countryName: artworkUpdate.artist.countryName,
                birthDate: artworkUpdate.artist.birthDate,
                deathDate: artworkUpdate.artist.deathDate
            };

            handleArtistSelect(assignedArtist);

            setEditTitle(artworkUpdate.artWorkTitle);
            setEditArtworkCreationDate(artworkUpdate.artworkCreationDate);
            setEditCategoryId(artworkUpdate.categoryId);
            setEditDescription(artworkUpdate.artDescription);
            setEditMedium(artworkUpdate.medium);
            setEditLocationId(artworkUpdate.locationId);
            setSelectedLocationId(artworkUpdate.locationId);
            setEditCurrency(artworkUpdate.currencyCode);
            setEditValue(artworkUpdate.estimatedValue);

            //Dimensions
            const dimensions: AssetDimensionValues = {
                height: artworkUpdate.height,
                width: artworkUpdate.width,
                depth: artworkUpdate.depth,
                dimensionUnit: artworkUpdate.dimensionUnits,
                weight: artworkUpdate.weight,
                weightUnit: artworkUpdate.weightUnits
            };

            //setFormValues(dimensions);

            setMeasurements(
                artworkUpdate?.measurements?.length
                    ? artworkUpdate.measurements.map(m => ({
                        artworkMeasurementId: m.artworkMeasurementId,
                        measurementType: m.measurementType,
                        height: m.height ?? "",
                        width: m.width ?? "",
                        depth: m.depth ?? "",
                        dimensionUnit: m.dimensionUnit ?? "cm",
                        weight: m.weight ?? "",
                        weightUnit: m.weightUnit ?? "kg",
                    }))
                    : [
                        {
                            artworkMeasurementId: null,
                            measurementType: ArtworkMeasurementType.Artwork,
                            height: "",
                            width: "",
                            depth: "",
                            dimensionUnit: "cm",
                            weight: "",
                            weightUnit: "kg",
                        }
                    ]
            );

            //Provenance
            const dateString = artworkUpdate.dateAcquired ? new Date(artworkUpdate.dateAcquired).toLocaleDateString() : undefined;
            const provDetails: ArtworkProvenance = {
                dateAcquired: dateString,
                sourceId: artworkUpdate.sourceId ?? 0,
                sourceName: artworkUpdate.sourceName,
                provenance: artworkUpdate.provenance ?? '',
                notes: artworkUpdate.sourceDescription ?? '',
            }

            setProvenanceDetails(provDetails);
        }

    }, [artworkUpdate]);

    React.useEffect(() => {
        if (!isAuthenticated || activeClientId == null) return;

        if (isOpen) {
            if (!artworkId) resetForm();

            const getAdditionalRefData = async () => {
                getData().catch(() => { });
            };

            getAdditionalRefData();

            refresh();
        }

    }, [isOpen]);

    const locationTree = React.useMemo(() => (locations ? locations : []), [locations]);

    // Loading flags
    const stillLoading = orgReq.loading || artistReq.loading || catReq.loading || loadingAny;

    // Error flags
    const errorsFlags =
        orgReq.error || undefined;

    const handleProvenance_Cancel = () => {
        //setArtworkProvenanceViewMode("view");
    }

    if (stillLoading) return <p>Loading...</p>;

    if (!stillLoading && errorsFlags) {
        return (
            <div className="bg-yellow-50 border border-yellow-300 p-3 rounded">
                <p>{errorsFlags}</p>
                <button className="mt-2 px-3 py-1 rounded border" onClick={() => refresh()}>
                    Retry loading
                </button>
            </div>
        );
    }



    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth fullScreen={isMobile} maxWidth={isMobile ? false : maxWidth}
            scroll="paper"
            PaperProps={{
                sx: {
                    maxHeight: isMobile ? "100vh" : "90vh",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: isMobile ? 0 : 2,
                },
            }}>
            <DialogTitle
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: 16, sm: 18 },
                }}>
                {isEditMode ? 'Edit Artwork' : 'Add New Artwork'}
            </DialogTitle>
            <hr></hr>
            <DialogContent
                dividers
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 2 },
                }}
            >
                {/* Form grid */}
                <Box
                    component="form"
                    noValidate
                    sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: isEditMode ? "1fr" : "1fr 1fr",
                        },
                    }}
                >
                    

                    {/* LEFT COLUMN – always */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {/* Error Panel*/}
                        <Box>
                            <ErrorSummary errors={errors} title="Artwork Errors" />
                        </Box>
                        {/* Core details */}
                                <Paper elevation={2} sx={{ p: 2 }}>
                                    <Typography variant="caption">General Item Information</Typography>
                                    <Paper elevation={2} sx={{ p: 2 }}>
                                        <Typography variant="caption">Artist Details</Typography>
                                        <ArtistSelector
                                            selectedArtist={selectedArtist}
                                            onArtistSelect={handleArtistSelect}
                                            allowCreate={true}
                                            allowSearch={true}
                                            required={true}
                                        ></ArtistSelector>

                                        {/* Selected Artist Preview */}
                                        {selectedArtist && (
                                            <div className="selected-artist-preview">
                                        <Typography variant="caption">Selected Artist:</Typography>
                                                <div className="artist-info">
                                                    <strong>{selectedArtist.artistName}</strong>
                                                    {selectedArtist.countryName && (
                                                        <span> • {selectedArtist.countryName}</span>
                                                    )}
                                                    {selectedArtist.artistPeriod && (
                                                        <span> • {selectedArtist.artistPeriod}</span>
                                                    )}
 
                                                </div>
                                            </div>
                                        )}
                                    </Paper>
                                    <br></br>

                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <TextField
                                            fullWidth
                                            label="Title"
                                            size="small"
                                            margin="normal"
                                            value={editTitle}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                            className="required-field"
                                        />
                                        <TextField
                                            label="Date"
                                            size="small"
                                            margin="normal"
                                            value={editArtworkCreationDate}
                                            onChange={(e) => setEditArtworkCreationDate(e.target.value)}
                                        />
                                    </Box>

                                    <div>
                                        <CategoryPickerField
                                            categories={categories}
                                            value={editCategoryId}
                                            onChange={(id) => setEditCategoryId(id)}
                                            label="Category"
                                            required={true}
                                            pickerVariant="dialog"
                                            allowClear={false}
                                        />
                                    </div>

                                    <TextField
                                        fullWidth
                                        label="Medium"
                                        size="small"
                                        margin="normal"
                                        value={editMedium}
                                        onChange={(e) => setEditMedium(e.target.value)}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Description"
                                        size="small"
                                        margin="normal"
                                        multiline
                                        rows={3}
                                        value={editDescription}
                                        onChange={(e) => setEditDescription(e.target.value)}
                                        className="required-field"
                                    />
                                    <br></br>
                                    <Paper elevation={2} sx={{ p: 2 }}>
                                <Typography variant="caption">Dimensions</Typography>
                                <div className="measurements-container">
                                            {artworkMeasurement && (
                                                <AssetDimensions
                                                    title="Artwork"
                                                    values={toDimensionValues(artworkMeasurement)}
                                        onChange={(field, value) =>
                                            handleMeasurementChange(
                                                ArtworkMeasurementType.Artwork,
                                                field,
                                                value
                                            )
                                        }
                                                />
                                )}

                                {framedMeasurement ? (
                                    <AssetDimensions
                                        title="Framed"
                                        values={toDimensionValues(framedMeasurement)}
                                        onChange={(field, value) =>
                                            handleMeasurementChange(
                                                ArtworkMeasurementType.Framed,
                                                field,
                                                value
                                            )
                                        }
                                        onRemove={() => removeFramedMeasurement()}
                                    />
                                ) : (
                                    <Button
                                        variant="outlined"
                                        onClick={addFramedMeasurement}
                                    >
                                        Add framed dimensions
                                    </Button>
                                    )}
                                </div>
                                    </Paper>
                                    <br></br>

  
                            <Paper elevation={2} sx={{ p: 2 }} className="required-field">
                                        <Typography variant="caption">Location</Typography>
                                        {locationTree.length > 0 ? (
                                            <TreeViewerMUI
                                                locations={locationTree}
                                                selectedId={editLocationId?.toString() ?? undefined}
                                        onItemClick={(id) => handleLocationChange(id)}
                                            />
                                        ) : (
                                            <p>Loading location tree...</p>
                                        )}
                                    </Paper>
                                </Paper>
                    </Box>

                    {!isEditMode &&

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            
                            <Box>
                                <Paper elevation={2} sx={{ p: 2 }}>
                                    <Typography variant="caption">Acqusition Details</Typography>

                                    <TextField
                                        fullWidth
                                        select
                                        label="Acquisition Currency"
                                        size="small"
                                        margin="normal"
                                        value={editCurrency}
                                        onChange={(e) => setEditCurrency(e.target.value)}
                                        className="required-field"
                                    >
                                        {currencies?.map((curr: any) => (
                                            <MenuItem key={curr.currencyCode} value={curr.currencyCode}>
                                                {curr.currencyCode}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <div>
                                        <ValueInput
                                            currencySymbol={editCurrency}
                                            label="Acquisition Cost"
                                            allowNegative={false}
                                            decimalPlaces={2}
                                            className="required-field"
                                            onValueChange={handleAmountChange} />
                                    </div>
                                </Paper>
                            </Box>

                            <ProvenanceForm
                                ref={provenanceRef}
                                mode="edit"
                                showHeaderActions={false}
                                initialValues={provenanceDetails}
                                onCancel={handleProvenance_Cancel}
                            />
                        </Box>
                    }

                </Box>

            </DialogContent>
            <DialogActions
                sx={{
                    px: { xs: 3, sm: 3 },
                    py: { xs: 1.5, sm: 2 },
                    position: isMobile ? "sticky" : "static",
                    bottom: 0,
                    backgroundColor: "background.paper",
                    borderTop: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Button variant="contained"  onClick={onClose} size={isMobile ? "lg" : "sm"}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    size={isMobile ? "lg" : "sm"}
                >
                    Save
                </Button>

            </DialogActions>
        </Dialog>
    );
};

export default ArtworkManagement;